import { CleverTap } from '@awesome-cordova-plugins/clevertap';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

class CleverTapService {
  constructor() {
    this.clevertap = CleverTap;
  }

  // Initialize CleverTap
  async initialize() {
    try {
      if (Capacitor.isNativePlatform()) {
        await this.initializePushNotifications();
        await this.clevertap.setDebugLevel(3); // Set debug level (0-3)
        await this.clevertap.createNotificationChannel(
          'CROBO',
          'CROBO',
          'CROBO Notifications',
          5,
          true
        );
        console.log('CleverTap initialized successfully');
      }
    } catch (error) {
      console.error('Error initializing CleverTap:', error);
    }
  }

  // Initialize push notifications
  async initializePushNotifications() {
    try {
      if (!Capacitor.isNativePlatform()) {
        console.log('Push notifications not supported on web platform');
        return;
      }

      // Request permission
      const permissionStatus = await PushNotifications.requestPermissions();
      
      if (permissionStatus.receive === 'granted') {
        // Register for push notifications
        await PushNotifications.register();
        
        // Add listeners for push notification events
        this.addPushNotificationListeners();
      } else {
        console.log('Push notification permission denied');
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  }

  // Add push notification listeners
  addPushNotificationListeners() {
    // Handle registration success
    PushNotifications.addListener('registration', (token) => {
      console.log('Push registration success, token:', token.value);
      this.extractAndSetPushToken(token.value);
    });

    // Handle registration error
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Error on registration:', error);
    });

    // Handle push notification received
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
    });

    // Handle push notification action
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('Push notification action performed:', notification);
    });
  }

  // Request push notification permission
  async requestPushPermission(showFallbackDialog = true) {
    try {
      if (!Capacitor.isNativePlatform()) {
        console.log('Push notifications not supported on web platform');
        return false;
      }

      // Request permission
      await this.clevertap.promptForPushPermission(showFallbackDialog);
      
      // Check if permission was granted
      const isGranted = await this.checkPushPermission();
      console.log('Push notification permission status:', isGranted);
      
      return isGranted;
    } catch (error) {
      console.error('Error requesting push notification permission:', error);
      return false;
    }
  }

  // Check push notification permission status
  async checkPushPermission() {
    try {
      if (!Capacitor.isNativePlatform()) {
        return false;
      }

      return new Promise((resolve) => {
        this.clevertap.isPushPermissionGranted((isGranted) => {
          console.log('Push notification permission check result:', isGranted);
          resolve(isGranted);
        });
      });
    } catch (error) {
      console.error('Error checking push notification permission:', error);
      return false;
    }
  }

  // Record user profile
  async recordUserProfile(profile) {
    try {
      if (Capacitor.isNativePlatform()) {
        await this.clevertap.onUserLogin(profile);
        await this.clevertap.registerPush()
        console.log('User profile recorded successfully');
      }
    } catch (error) {
      console.error('Error recording user profile:', error);
    }
  }

  // Record event
  async recordEvent(eventName, eventData) {
    try {
      if (Capacitor.isNativePlatform()) {
        await this.clevertap.recordEventWithName(eventName, eventData);
        console.log(`Event ${eventName} recorded successfully`);
      }
    } catch (error) {
      console.error(`Error recording event ${eventName}:`, error);
    }
  }

  // Record charged event
  async recordChargedEvent(chargeDetails, items) {
    try {
      if (Capacitor.isNativePlatform()) {
        await this.clevertap.recordChargedEventWithDetails(chargeDetails, items);
        console.log('Charged event recorded successfully');
      }
    } catch (error) {
      console.error('Error recording charged event:', error);
    }
  }

  // Get user profile
  async getUserProfile() {
    try {
      if (Capacitor.isNativePlatform()) {
        const profile = await this.clevertap.getProfile();
        return profile;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  // Set push notification token
  async setPushToken(token) {
    try {
      if (Capacitor.isNativePlatform()) {
        await this.clevertap.setPushToken(token);
        console.log('Push token set successfully');
        return true;
      }
      console.log('Push token not set - not a native platform');
      return false;
    } catch (error) {
      console.error('Error setting push token:', error);
      return false;
    }
  }

  // Extract and set push token from various sources
  async extractAndSetPushToken(tokenData) {
    try {
      if (!Capacitor.isNativePlatform()) {
        console.log('Push token not set - not a native platform');
        return false;
      }

      let token;
      
      // Handle different token formats
      if (typeof tokenData === 'string') {
        token = tokenData;
      } else if (tokenData?.token) {
        token = tokenData.token;
      } else if (tokenData?.registrationId) {
        token = tokenData.registrationId;
      } else if (tokenData?.deviceToken) {
        token = tokenData.deviceToken;
      } else {
        console.error('Invalid token data format:', tokenData);
        return false;
      }

      // Set the extracted token
      return await this.setPushToken(token);
    } catch (error) {
      console.error('Error extracting and setting push token:', error);
      return false;
    }
  }
}

export default new CleverTapService(); 